import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash2, Plus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import React, { useState, useEffect } from "react";
import EditStaffDialog from "./EditStaffDialogue";
import AddStaffDialog from "./AddStaffDialogue";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

interface StaffUser {
  id: number;
  name: string;
  email: string;
  role: string;
  lastLogin: string;
  status: string;
}

interface StaffFormValues {
  role?: string;
  name?: string;
  email?: string;
  password?: string;
  status?: string;
}

const StaffManagement = () => {
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<StaffUser | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState<number | null>(null);

  const fetchStaffUsers = React.useCallback(
    async (opts?: {
      page?: number;
      pageSize?: number;
      searchTerm?: string;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const reqPage = opts?.page ?? page;
        const reqPageSize = opts?.pageSize ?? pageSize;
        const reqSearchTerm = opts?.searchTerm ?? searchTerm;
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/staff/get`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              page: reqPage,
              pageSize: reqPageSize,
              searchTerm: reqSearchTerm,
            }),
          }
        );
        const result = await response.json();
        if (response.ok) {
          type ApiStaffUser = StaffUser & { last_login?: string };
          const formatDateTime = (dateStr?: string) => {
            if (!dateStr) return "-";
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return "-";
            const pad = (n: number) => n.toString().padStart(2, "0");
            return (
              pad(d.getDate()) +
              "-" +
              pad(d.getMonth() + 1) +
              "-" +
              d.getFullYear() +
              " " +
              pad(d.getHours()) +
              ":" +
              pad(d.getMinutes()) +
              ":" +
              pad(d.getSeconds())
            );
          };
          setStaffUsers(
            (result.users || []).map((u: ApiStaffUser) => ({
              ...u,
              lastLogin: formatDateTime(u.last_login),
            }))
          );
          setTotal(result.total || 0);
        } else {
          setError(result.error || "Failed to fetch staff users.");
        }
      } catch (err) {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, searchTerm]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchStaffUsers({ page, pageSize, searchTerm: debouncedSearchTerm });
  }, [page, pageSize, debouncedSearchTerm, fetchStaffUsers]);

  const handleAddStaff = async (staff: StaffFormValues) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/staff/add`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(staff),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast({
          title: "Staff member added",
          description:
            result.message || `${staff.name} has been added successfully.`,
        });
        setPage(1);
        fetchStaffUsers({ page: 1, pageSize, searchTerm });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to add staff member.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditStaff = async (staff: StaffFormValues & { id: number }) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/staff/update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(staff),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast({
          title: "Staff member updated",
          description:
            result.message || `${staff.name} has been updated successfully.`,
        });
        fetchStaffUsers({ page, pageSize, searchTerm: debouncedSearchTerm });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to update staff member.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditClick = (user: StaffUser) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  };

  const handleDeleteUser = (userId: number) => {
    setUserIdToDelete(userId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!userIdToDelete) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/staff/delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ id: userIdToDelete }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast({
          title: "Staff member deleted",
          description: result.message || "Staff member has been deleted.",
        });
        setShowDeleteDialog(false);
        setUserIdToDelete(null);
        fetchStaffUsers({ page, pageSize, searchTerm: debouncedSearchTerm });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete staff member.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Staff Management</span>
            <Button variant="gradient" onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add New Staff
            </Button>
          </CardTitle>
          <CardDescription>
            Manage staff accounts, permissions, and access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="Search by name, email, or ID"
                className="input input-bordered px-2 py-1 rounded border"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                style={{ minWidth: 200 }}
                autoComplete="off"
              />
            </div>
          </div>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading staff users...
            </div>
          ) : error ? (
            <div className="py-8 text-center text-destructive">{error}</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffUsers.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground"
                      >
                        No staff users found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    staffUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{user.role}</Badge>
                        </TableCell>
                        <TableCell>{user.lastLogin}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              user.status === "Active" ? "default" : "secondary"
                            }
                          >
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditClick(user)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <div className="text-sm text-muted-foreground mt-2">
                {(() => {
                  const rowCount = total;
                  const pageIndex = page - 1;
                  const totalCount = total;
                  const start = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
                  const end =
                    rowCount === 0
                      ? 0
                      : Math.min((pageIndex + 1) * pageSize, rowCount);
                  if (debouncedSearchTerm && totalCount > staffUsers.length) {
                    return `Showing ${start} to ${end} of ${staffUsers.length} entries (filtered from ${totalCount} total entries)`;
                  } else {
                    return `Showing ${start} to ${end} of ${rowCount} entries`;
                  }
                })()}
              </div>
              <div className="flex justify-end items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((old) => Math.max(1, old - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((old) => old + 1)}
                  disabled={page >= Math.ceil(total / pageSize)}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage(Math.max(1, Math.ceil(total / pageSize)))
                  }
                  disabled={page >= Math.ceil(total / pageSize)}
                >
                  Last
                </Button>
                <span className="ml-4">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  {[5, 10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AddStaffDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onAddStaff={handleAddStaff}
      />

      <EditStaffDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        handleEditStaff={handleEditStaff}
        staffUser={selectedUser}
      />

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Staff Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this staff member? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteUser}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StaffManagement;
